import React, {useCallback, useEffect, useState} from "react";
import {Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Tooltip, Typography} from "@mui/material";
import {Add as AddIcon, LockOutlined as LockIcon} from "@mui/icons-material";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {SistemaLogo} from "../Sistemas/components/SistemaVisual";
import MetricasLayout from "./components/MetricasLayout";
import {listMetricas} from "./MetricasService";
import {APURACOES, AO_ATINGIR, FORMAS, NATUREZAS, planosQueUsam, plural, rotulo, unidadeDeCobranca} from "./metricasRegras";

const celula = {px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', verticalAlign: 'top', fontSize: 14};

const LinhaDaMetrica = ({metrica}) => {
    const navigate = useNavigate();
    const destino = `/metricas/${metrica.id}`;
    const sistemas = metrica.sistemas || [];
    const planos = planosQueUsam(metrica);
    const bloqueia = metrica.ao_atingir === 'BLOQUEAR';

    return (
        <Box component="tr" onClick={() => navigate(destino)} sx={{cursor: 'pointer', '&:hover': {bgcolor: 'action.hover'}}}>
            <Box component="td" sx={celula}>
                <Typography component={RouterLink} to={destino} onClick={(event) => event.stopPropagation()} variant="body2" fontWeight={700} sx={{color: 'primary.main', textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}>
                    {metrica.nome}
                </Typography>
                {metrica.descricao ? <Typography variant="caption" color="text.secondary" display="block">{metrica.descricao}</Typography> : null}
            </Box>
            <Box component="td" sx={{...celula, fontFamily: 'monospace'}}>{metrica.chave}</Box>
            <Box component="td" sx={{...celula, whiteSpace: 'nowrap'}}>{rotulo(NATUREZAS, metrica.natureza)}</Box>
            <Box component="td" sx={{...celula, whiteSpace: 'nowrap'}}>{rotulo(APURACOES, metrica.apuracao)}</Box>
            <Box component="td" sx={celula}>
                <Chip size="small" variant="outlined" color={bloqueia ? 'default' : 'info'} icon={bloqueia ? <LockIcon fontSize="small" /> : undefined} label={rotulo(AO_ATINGIR, metrica.ao_atingir)} />
            </Box>
            <Box component="td" sx={{...celula, whiteSpace: 'nowrap'}}>
                {rotulo(FORMAS, metrica.forma_cobranca)}
                {metrica.forma_cobranca === 'BLOCO' ? <Typography variant="caption" color="text.secondary" display="block">por {unidadeDeCobranca(metrica)}</Typography> : null}
            </Box>
            <Box component="td" sx={celula}>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {sistemas.map((sistema) => (
                        <Tooltip key={sistema.id} title={sistema.nome}>
                            <span aria-label={sistema.nome}><SistemaLogo sistema={sistema} tamanho={22} /></span>
                        </Tooltip>
                    ))}
                    {!sistemas.length && !planos ? <Typography variant="body2" color="text.secondary">Ninguém</Typography> : null}
                </Stack>
                {planos ? <Typography variant="caption" color="text.secondary" display="block">{plural(planos, 'plano', 'planos')}</Typography> : null}
            </Box>
        </Box>
    );
};

/*
 * Cadastro de métricas (BBF-167 a, protótipo v2 "Métricas"): o que pode ser medido, limitado e cobrado nos planos.
 * A métrica é global; o sistema aponta as que usa, e o plano define limites e valores.
 */
export default function MetricasPage({openParent = true}) {
    const navigate = useNavigate();
    const [estado, setEstado] = useState({carregando: true, erro: '', metricas: []});

    const carregar = useCallback(async () => {
        setEstado((atual) => ({...atual, carregando: true, erro: ''}));
        try {
            setEstado({carregando: false, erro: '', metricas: await listMetricas()});
        } catch (error) {
            setEstado({carregando: false, erro: getBackendErrorMessage(error, 'Não foi possível carregar as métricas.'), metricas: []});
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    return (
        <MetricasLayout openParent={openParent}>
            <Stack spacing={3}>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={2} justifyContent="space-between" alignItems={{md: 'center'}}>
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight={700}>Métricas</Typography>
                        <Typography variant="body1" color="text.secondary">O que pode ser medido, limitado e cobrado nos planos.</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/metricas/nova')} sx={{flexShrink: 0}}>Nova métrica</Button>
                </Stack>

                {estado.erro ? <Alert severity="error">{estado.erro}</Alert> : null}

                {estado.carregando ? (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{py: 6}}>
                        <CircularProgress size={20} />
                        <Typography color="text.secondary">Carregando as métricas…</Typography>
                    </Stack>
                ) : (
                    <Paper variant="outlined" sx={{overflow: 'hidden'}}>
                        <Box sx={{overflowX: 'auto'}}>
                            <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', minWidth: 900}}>
                                <Box component="thead">
                                    <Box component="tr" sx={{'& th': {px: 2, py: 1.25, textAlign: 'left', color: 'text.secondary', fontWeight: 500, fontSize: 13}}}>
                                        <th>Métrica</th>
                                        <th>Chave</th>
                                        <th>Natureza</th>
                                        <th>Apuração</th>
                                        <th>Ao atingir (padrão)</th>
                                        <th>Cobrança</th>
                                        <th>Usada por</th>
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {estado.metricas.map((metrica) => <LinhaDaMetrica key={metrica.id} metrica={metrica} />)}
                                    {!estado.metricas.length && !estado.erro ? (
                                        <Box component="tr">
                                            <Box component="td" colSpan={7} sx={{...celula, py: 5, textAlign: 'center', color: 'text.secondary'}}>
                                                Nenhuma métrica ainda. Cadastre a primeira em Nova métrica.
                                            </Box>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Stack>
        </MetricasLayout>
    );
}
